import SmartLabel from 'components/v2/labels/SmartLabel';
import { useField } from 'formik';
import React, { useRef, ChangeEvent, KeyboardEvent, useEffect, useId } from 'react';
import styled from 'styled-components';

interface InputCodeProps {
  name: string;
  length: number;
  label: string;
  loading: boolean;
  onComplete: (code: string) => void;
}

const InputCode: React.FC<InputCodeProps> = ({ name, length, label, loading, onComplete }) => {
  const id = useId();
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const [field, meta, helpers] = useField(name);
  const code = Array.from({ length }, (_, i) => field.value?.[i] ?? '');

  useEffect(() => {
    inputs.current = inputs.current.slice(0, length);
  }, [length]);

  const checkAndComplete = (arr: string[]) => {
    if (arr.every((c: string) => c !== '') && arr[length - 1] !== '') {
      onComplete(arr.join(''));
    }
  };

  const processInput = (e: ChangeEvent<HTMLInputElement>, slot: number): void => {
    let num = e.target.value.replace(/[^0-9]/g, '');
    if (num.length > 1) num = num.slice(-1);

    const newCode = [...code];
    newCode[slot] = num;
    helpers.setValue(newCode.join(''));
    checkAndComplete(newCode);

    if (num && slot < length - 1) {
      inputs.current[slot + 1]?.focus();
      inputs.current[slot + 1]?.select();
    }
  };

  const onKeyUp = (e: KeyboardEvent<HTMLInputElement>, slot: number): void => {
    if (e.key === 'Backspace' && !code[slot] && slot > 0) {
      inputs.current[slot - 1]?.focus();
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>, slot: number): void => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    const numbersOnly = pastedData.replace(/[^0-9]/g, '');
    if (!numbersOnly) return;
    const newCode = [...code];
    let currentSlot = slot;
    for (let i = 0; i < numbersOnly.length && currentSlot < length; i++) {
      newCode[currentSlot] = numbersOnly[i];
      currentSlot++;
    }
    helpers.setValue(newCode.join(''));
    checkAndComplete(newCode);

    if (currentSlot < length && numbersOnly.length > 0) {
      inputs.current[currentSlot]?.focus();
    } else if (currentSlot === length && newCode.every((c) => c !== '')) {
      inputs.current[length - 1]?.focus();
    }
  };

  return (
    <CodeInputDiv>
      {label && <SmartLabel htmlFor={id} label={label} hasValue={field.value} />}
      <CodeInputs>
        {code.map((num: string | number, idx: number) => {
          return (
            <StyledCodeInput
              key={idx}
              name={name}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={num}
              autoFocus={!code[0]?.length && idx === 0}
              onBlur={field.onBlur}
              onChange={(e) => processInput(e, idx)}
              onKeyUp={(e) => onKeyUp(e, idx)}
              onPaste={(e) => onPaste(e, idx)}
              onFocus={(e) => e.target.select()}
              ref={(ref) => {
                inputs.current[idx] = ref;
              }}
              disabled={loading}
            />
          );
        })}
      </CodeInputs>
    </CodeInputDiv>
  );
};

export default InputCode;

const CodeInputDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
`;

const CodeInputs = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
`;

const StyledCodeInput = styled.input`
  padding: 1rem;
  width: 70px;
  border: 1px solid
    ${(props) => {
      if (props.disabled) {
        return props.theme.colors.elementsFilledInput;
      }
      return props.theme.colors.elementsInputBorder;
    }};
  border-radius: 2px;
  margin: 0 16px 0 0;
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.elementsFilledInput : props.theme.colors.primaryWhite};
  color: ${(props) => (props.disabled ? props.theme.colors.primaryBlack70 : props.theme.colors.black)};
  &:focus {
    border-color: ${(props) => (props.disabled ? 'transparent' : props.theme.colors.primaryOrange)};
  }

  &:focus-visible {
    outline: 2px solid
      ${(props) => (props.disabled ? props.theme.colors.elementsInputBorder : props.theme.colors.primaryOrange)};
  }

  &:hover {
    border-color: ${(props) => (props.disabled ? 'transparent' : props.theme.colors.primaryOrange)};
  }

  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  text-align: center;
  line-height: normal;
  height: 44px;
`;
